import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowRight, Sparkles, Check, Zap } from "lucide-react";
import { motion } from "framer-motion";
import PromptGenerator from "@/components/PromptGenerator";
import Testimonials from "@/components/Testimonials";
import FAQs from "@/components/FAQs";

const HERO_BG = "https://images.unsplash.com/photo-1672080070762-764c74ee1227?crop=entropy&cs=srgb&fm=jpg&w=1600";

const stats = [
  { k: "7", v: "Core modules" },
  { k: "3", v: "Access tiers" },
  { k: "9+", v: "Toolkit assets" },
];

export default function AcademyHome() {
  const [modules, setModules] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [toolkit, setToolkit] = useState([]);

  useEffect(() => {
    http.get("/courses").then(({ data }) => setModules(data.items.sort((a, b) => (a.number || "").localeCompare(b.number || ""))));
    http.get("/tiers").then(({ data }) => setTiers(data.items));
    http.get("/toolkit").then(({ data }) => setToolkit(data.items));
  }, []);

  return (
    <div data-testid="academy-home">
      {/* HERO */}
      <section className="relative overflow-hidden pt-12 pb-24 lg:pt-20 lg:pb-32">
        <div className="absolute inset-0 -z-10 opacity-35" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 -z-10" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.5), rgba(5,5,5,0.95))" }} />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="label-eyebrow mb-6">Half 01 · The Academy</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="lg:col-span-9 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] font-display-ai"
            >
              The complete AI-powered system for{" "}
              <span className="text-[#00FFC2]">generating content,</span>{" "}
              building digital products, and creating automated online income.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.8 }}
              className="lg:col-span-3 text-gray-300 text-base leading-relaxed"
            >
              No experience needed. No coding. No expensive software.
            </motion.p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#tiers" data-testid="academy-cta-tiers" className="btn-mint px-6 py-3.5 rounded-full text-sm inline-flex items-center gap-2">
              Get instant access <ArrowRight size={16} />
            </a>
            <Link to="/academy/prompts" data-testid="academy-cta-prompts" className="px-6 py-3.5 rounded-full border hairline text-sm hover:bg-white/5 inline-flex items-center gap-2">
              <Sparkles size={16} /> Open Prompt Lab
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-3 gap-6 border-t border-b hairline py-6">
            {stats.map((s) => (
              <div key={s.k}>
                <div className="text-3xl sm:text-4xl font-black tracking-tight">{s.k}</div>
                <div className="text-xs text-gray-400 mt-1 tracking-wide">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="label-eyebrow mb-3">The thesis</div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-display-ai leading-tight">
              Most creators don't need more motivation.{" "}
              <span className="text-[#00FFC2]">They need systems.</span>
            </h2>
          </div>
          <ul className="lg:col-span-6 space-y-3 self-end text-gray-300">
            {[
              "Run out of content ideas",
              "Overthink every post",
              "Don't know how to monetize",
              "Waste hours trying random strategies",
            ].map((p) => (
              <li key={p} className="flex items-start gap-3 text-base">
                <span className="text-[#00FFC2] mt-1">—</span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MODULES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <h2 className="lg:col-span-7 text-4xl sm:text-5xl font-black tracking-tight font-display-ai">
            Everything inside <span className="text-[#00FFC2]">AI Creator OS.</span>
          </h2>
          <p className="lg:col-span-5 text-gray-400 self-end">
            Seven modules. One ecosystem. Each layer compounds on the last so by the end you have
            infrastructure — not motivation.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => (
            <Link to="/academy/courses" key={m.slug} className="glass rounded-xl p-6 hover:-translate-y-1 transition group" data-testid={`module-${m.slug}`}>
              <div className="flex items-baseline justify-between mb-4">
                <span className="text-5xl font-black text-[#00FFC2]/30 group-hover:text-[#00FFC2]/60 transition">{m.number}</span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-gray-400">{m.level}</span>
              </div>
              <div className="text-xl font-semibold mb-2">{m.title}</div>
              <div className="text-sm text-gray-400 leading-relaxed">{m.tagline}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* TOOLKIT */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="glass rounded-2xl p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="label-eyebrow mb-3">The Toolkit</div>
              <h3 className="text-4xl font-black tracking-tight font-display-ai">
                Every asset, ready to deploy.
              </h3>
              <p className="text-gray-400 mt-4 leading-relaxed">
                The AI Creator Toolkit ships with every tier. Swipe files, templates, vaults —
                everything pre-built so you can stop building from scratch.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {toolkit.map((t) => (
                <div key={t} className="flex items-center gap-3 border hairline rounded-md px-4 py-3" data-testid={`toolkit-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Zap size={14} className="text-[#00FFC2]" />
                  <span className="text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROMPT LAB */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">Try it live</div>
            <h3 className="text-4xl font-black tracking-tight font-display-ai">
              The Prompt Lab.
            </h3>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Drop a topic. We engineer the prompt — role, task, context, constraints, output —
              ready to paste into any model.
            </p>
          </div>
          <div className="lg:col-span-7">
            <PromptGenerator />
          </div>
        </div>
      </section>

      {/* TIERS / PRICING */}
      <section id="tiers" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="label-eyebrow mb-3">Choose your access</div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-display-ai">
            Three tiers. <span className="text-[#00FFC2]">One ecosystem.</span>
          </h2>
          <p className="text-gray-400 mt-4 leading-relaxed">
            Whether you're starting out or building a full creator business, AI Creator OS scales
            with you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {tiers.map((t) => (
            <article
              key={t.slug}
              data-testid={`tier-${t.slug}`}
              className={`relative rounded-2xl p-7 flex flex-col ${
                t.popular ? "glass glow-mint" : "glass"
              }`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.25em] uppercase btn-mint rounded-full px-3 py-1">
                  Most Popular
                </div>
              )}
              <div className="text-2xl font-bold font-display-ai">{t.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-5xl font-black tracking-tight">${t.price}</span>
                <span className="text-xs text-gray-400">one-time</span>
              </div>
              <p className="text-sm text-gray-400 mt-4 leading-relaxed">{t.tagline}</p>

              <div className="mt-6">
                <div className="label-eyebrow mb-3">What you'll learn</div>
                <ul className="space-y-2">
                  {t.learn.map((l) => (
                    <li key={l} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={14} className="text-[#00FFC2] mt-1 flex-shrink-0" /> {l}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <div className="label-eyebrow mb-3">What's included</div>
                <ul className="space-y-2">
                  {t.included.map((l) => (
                    <li key={l} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={14} className="text-[#00FFC2] mt-1 flex-shrink-0" /> {l}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 pt-6 border-t hairline">
                <div className="label-eyebrow mb-2">Best for</div>
                <div className="text-xs text-gray-400">{t.best_for.join(" · ")}</div>
              </div>

              <a
                href={t.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`tier-cta-${t.slug}`}
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm ${
                  t.popular ? "btn-mint" : "border hairline hover:bg-white/5"
                }`}
              >
                {t.cta_label} <ArrowRight size={16} />
              </a>
            </article>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials accent="#00FFC2" title="From operators in the network" />

      {/* FAQ */}
      <FAQs category="academy" accent="#00FFC2" title="Everything you might ask" />

      {/* CLOSER */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-center">
        <h3 className="text-5xl sm:text-6xl font-black tracking-tight font-display-ai">
          Stop consuming. <span className="text-[#00FFC2]">Start building.</span>
        </h3>
        <p className="text-gray-400 mt-5 max-w-2xl mx-auto leading-relaxed">
          The internet rewards creators who move fast, build systems, and leverage automation. AI
          Creator OS gives you the infrastructure to do exactly that.
        </p>
        <a
          href="https://theaicreatoros.gumroad.com/l/creator"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="academy-closer-cta"
          className="mt-8 btn-mint inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm"
        >
          Get instant access <ArrowRight size={16} />
        </a>
      </section>
    </div>
  );
}
